import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {useDisclosure} from '@mantine/hooks';
import {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {EntityActiveStatusPanel} from 'src/pages/Modules/Clients/shared/EntityActiveStatusPanel';
import {FieldSchemaSummary} from 'src/pages/Modules/Inspections/FieldEditor/FieldSchemaSummary';
import {
  EquipmentTypeDocumentTitle,
  EquipmentTypeViewGeneralMeta,
  ResolveInspectionTypeSelections,
} from 'src/pages/Modules/Inspections/shared/equipmentTypeHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useDeleteEquipmentTypeMutation,
  useGetEquipmentTypeFieldsQuery,
  useGetEquipmentTypeQuery,
  useUpdateEquipmentTypeActiveStatusMutation,
} from 'src/redux/queries/equipmentType';
import {useGetInspectionTypeSelectOptionsQuery} from 'src/redux/queries/inspectionType';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2} from 'src/types/FormV2';
import {mapFormFieldSimpleResponseToFormFieldV2} from 'src/types/FormV2Mapping';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type EquipmentTypeViewTab = 'details' | 'fields';

export const EquipmentTypeViewPage = () => {
  const navigate = useAppNavigate();
  const {equipmentTypeId, view} = useParams();
  const {setAlert} = useAlert();
  const [isDeleting, setIsDeleting] = useState(false);
  const inspectionPerms = useInspectionPermissions();
  useSetDocumentTitle(EquipmentTypeDocumentTitle);

  const validId =
    equipmentTypeId !== undefined && !isNaN(+equipmentTypeId)
      ? +equipmentTypeId
      : undefined;

  const {data: viewItem, isLoading} = useGetEquipmentTypeQuery(validId!, {
    skip: !validId,
  });

  const {data: fetchedFields} = useGetEquipmentTypeFieldsQuery(validId!, {
    skip: !validId,
  });
  const fieldList = useMemo(
    () => (fetchedFields ?? []).map(mapFormFieldSimpleResponseToFormFieldV2),
    [fetchedFields]
  );

  const {data: inspectionTypes} = useGetInspectionTypeSelectOptionsQuery({
    start: 0,
    length: 10000,
    filters: [],
    filterFns: [],
    sorting: [],
  });
  const viewGeneralMeta = useMemo(
    () => EquipmentTypeViewGeneralMeta(inspectionTypes?.data ?? []),
    [inspectionTypes]
  );

  const viewFormData = useMemo<IFormDataV2>(
    () =>
      viewItem
        ? {
            ...viewItem,
            inspectionTypeIds: ResolveInspectionTypeSelections(
              viewItem.inspectionTypeIds,
              inspectionTypes?.data ?? []
            ),
          }
        : {},
    [viewItem, inspectionTypes]
  );

  const [deleteEquipmentType] = useDeleteEquipmentTypeMutation();
  const [updateActiveStatus, {isLoading: isTogglingActive}] =
    useUpdateEquipmentTypeActiveStatusMutation();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const {RenderedTabsComponent} = useUrlQueryTabV2<EquipmentTypeViewTab>({
    view: (view ?? '') as EquipmentTypeViewTab,
    defaultView: 'details',
    tabs: [
      {
        view: 'details',
        label: 'Details',
        component: (
          <Stack gap="lg">
            <FormViewComponentV2
              formData={viewFormData}
              formMeta={viewGeneralMeta}
            />

            {viewItem && inspectionPerms.update && (
              <EntityActiveStatusPanel
                entityLabel="Equipment Type"
                isActive={!!viewItem.active}
                loading={isTogglingActive}
                onToggle={() =>
                  updateActiveStatus({
                    id: viewItem.id,
                    active: !viewItem.active,
                  }).unwrap()
                }
              />
            )}
          </Stack>
        ),
      },
      {
        view: 'fields',
        label: 'Inspection Fields',
        component: <FieldSchemaSummary fieldList={fieldList} />,
      },
    ],
    onSelectView: nextView =>
      navigate(
        PathUtility.EquipmentTypesView(
          equipmentTypeId as string,
          nextView as EquipmentTypeViewTab
        ),
        {replace: true}
      ),
  });

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!equipmentTypeId) {
        setAlert('ID not found.', 'error');
        return;
      }

      setIsDeleting(true);

      await deleteEquipmentType(+equipmentTypeId).unwrap();

      setAlert('Equipment Type was successfully deleted.', 'success');

      navigate(PathUtility.Inspections('equipment-types'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (!viewItem) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <Alert color="red" title="Not Found">
          Equipment Type not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.Inspections('equipment-types')}
          fallbackLabel="Back to Equipment Types"
          breadcrumbs={[
            {
              label: 'Equipment Types',
              to: PathUtility.Inspections('equipment-types'),
            },
            {label: viewItem.name ?? 'Equipment Type'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title icon={SidebarLinkInspections.icon}>
            {viewItem.name ?? 'Equipment Type'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <PageHeader.Badge
              color={viewItem.active ? 'green' : 'yellow'}
              variant="light">
              {viewItem.active ? 'Active' : 'Inactive'}
            </PageHeader.Badge>
          </PageHeader.Badges>

          <PageHeader.Actions>
            {inspectionPerms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() =>
                  navigate(PathUtility.EquipmentTypesEdit(equipmentTypeId!))
                }>
                Edit
              </PageHeader.Action>
            )}
            {inspectionPerms.delete && (
              <PageHeader.Action
                variant="delete"
                onClick={() => confirmDeleteHandlers.open()}
                loading={isDeleting}>
                Delete
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        {RenderedTabsComponent}
      </Stack>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this equipment type?"
        body="Deletion is refused if any devices reference this type — deactivate it instead to retire it without breaking device history."
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.EquipmentTypesView(':equipmentTypeId', ':view?'),
  element: <EquipmentTypeViewPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
